import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #isSyncingFromMedia = false;
  #mediaVariantMap = new Map();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.#initializeMediaVariantMap();

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    target?.addEventListener('product:media-change', this.#handleMediaChange, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the matching media without reordering the gallery.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.#isSyncingFromMedia) {
      this.#isSyncingFromMedia = false;
      return;
    }

    const featuredMediaId = this.#normalizeMediaId(event.detail.resource?.featured_media?.id);
    if (!featuredMediaId || !this.slideshow?.slides?.length) return;

    const selectedSlide = this.slideshow.slides.find((slide) => {
      return this.#normalizeMediaId(slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId) === featuredMediaId;
    });

    const slideId = selectedSlide?.getAttribute('slide-id');
    if (!slideId) return;

    this.slideshow.select({ id: slideId }, undefined, { animate: false });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Syncs media changes to a variant selection.
   * @param {CustomEvent<{mediaId?: string}>} event
   */
  #handleMediaChange = (event) => {
    if (this.#isSyncingFromMedia) {
      return;
    }

    const eventMediaId = this.#normalizeMediaId(event.detail?.mediaId);
    const activeMediaId = this.#normalizeMediaId(this.#getActiveMediaId());
    const mediaIdCandidates = [eventMediaId, activeMediaId].filter((candidate, index, allCandidates) => {
      return Boolean(candidate) && allCandidates.indexOf(candidate) === index;
    });

    if (mediaIdCandidates.length === 0) {
      return;
    }

    const mappedMediaId = mediaIdCandidates.find((candidateMediaId) => this.#mediaVariantMap.has(candidateMediaId));
    const variantId = mappedMediaId ? this.#mediaVariantMap.get(mappedMediaId) : undefined;

    if (!variantId) {
      return;
    }

    const sectionRoot = this.closest('.shopify-section, dialog');
    const variantControl =
      sectionRoot?.querySelector(`.variant-picker__form input[data-variant-id="${variantId}"]`) ||
      sectionRoot?.querySelector(`.variant-picker__form option[data-variant-id="${variantId}"]`);

    if (!variantControl) return;

    this.#isSyncingFromMedia = true;

    try {
      if (variantControl instanceof HTMLOptionElement) {
        const select = variantControl.closest('select');
        if (!(select instanceof HTMLSelectElement)) return;
        select.value = variantControl.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      variantControl.click();
    } finally {
      this.#isSyncingFromMedia = false;
    }
  };

  /**
   * Initializes media -> variant ID mapping from JSON rendered by Liquid.
   */
  #initializeMediaVariantMap() {
    this.#mediaVariantMap.clear();

    const sectionRoot = this.closest('.shopify-section, dialog') || document;
    const variantsScript = sectionRoot.querySelector('[data-vairay-variants]') || document.querySelector('[data-vairay-variants]');
    const textContent = variantsScript?.textContent?.trim();

    if (!textContent) return;

    try {
      const variants = JSON.parse(textContent);
      const ambiguousMediaIds = new Set();

      if (!Array.isArray(variants)) return;

      variants.forEach((variant) => {
        const mediaId = variant?.featured_media?.id;
        const variantId = variant?.id;

        if (!mediaId || !variantId) return;

        const key = String(mediaId);
        if (ambiguousMediaIds.has(key)) return;
        const mappedVariantId = this.#mediaVariantMap.get(key);

        if (!mappedVariantId) {
          this.#mediaVariantMap.set(key, String(variantId));
          return;
        }

        if (mappedVariantId !== String(variantId)) {
          ambiguousMediaIds.add(key);
          this.#mediaVariantMap.delete(key);
        }
      });

    } catch (error) {
      console.error('Unable to parse media to variant mapping.', error);
    }
  }

  #getActiveMediaId() {
    const activeMedia = this.querySelector('slideshow-slide[aria-hidden="false"] .product-media[data-media-id]');

    return activeMedia?.dataset.mediaId;
  }

  /**
   * @param {string | number | undefined | null} mediaId
   */
  #normalizeMediaId(mediaId) {
    if (mediaId === undefined || mediaId === null) return undefined;

    const normalizedMediaId = String(mediaId).trim();

    return normalizedMediaId.length ? normalizedMediaId : undefined;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
